import React, { useEffect, useRef, useState } from 'react';
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  FileScan,
  FileUp,
  Images,
  Loader2,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { createMedicalRecord } from '../../services/MedicalRecordService';
import { MedicalRecordFileStorageService } from '../../services/MedicalRecordFileStorageService';
import type { Client, User } from '../../services/UserService';
import { createExamPdfFile } from '../../utils/examDocumentPdf';

const MAX_EXAM_PAGES = 20;
const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024;

interface ExamImagePage {
  id: string;
  file: File;
  previewUrl: string;
}

interface ExamUploadShortcutProps {
  patientId: string;
  patientName?: string;
  user: User;
  selectedClient: Client;
  onSaved?: () => void | Promise<void>;
  hideTrigger?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const toLocalDateTimeInput = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

const isImageFile = (file: File) =>
  file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);

const ExamUploadShortcut: React.FC<ExamUploadShortcutProps> = ({
  patientId,
  patientName,
  user,
  selectedClient,
  onSaved,
  hideTrigger = false,
  isOpen: controlledIsOpen,
  onOpenChange,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = (nextIsOpen: boolean) => {
    if (controlledIsOpen === undefined) setInternalIsOpen(nextIsOpen);
    onOpenChange?.(nextIsOpen);
  };
  const [title, setTitle] = useState('Exame');
  const [description, setDescription] = useState('');
  const [examDate, setExamDate] = useState(toLocalDateTimeInput);
  const [pages, setPages] = useState<ExamImagePage[]>([]);
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const pagesRef = useRef<ExamImagePage[]>([]);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(
    () => () => {
      pagesRef.current.forEach((page) => URL.revokeObjectURL(page.previewUrl));
    },
    [],
  );

  const clearPages = () => {
    pagesRef.current.forEach((page) => URL.revokeObjectURL(page.previewUrl));
    pagesRef.current = [];
    setPages([]);
  };

  const resetForm = () => {
    clearPages();
    setSelectedPdf(null);
    setTitle('Exame');
    setDescription('');
    setExamDate(toLocalDateTimeInput());
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setIsOpen(false);
    resetForm();
  };

  const addImages = (files: File[]) => {
    const imageFiles = files.filter(isImageFile);
    if (imageFiles.length === 0) {
      toast.error('Selecione imagens válidas para as páginas do exame.');
      return;
    }

    const currentPages = pagesRef.current;
    const availableSlots = MAX_EXAM_PAGES - currentPages.length;
    if (availableSlots <= 0) {
      toast.error(`Cada exame pode ter no máximo ${MAX_EXAM_PAGES} páginas.`);
      return;
    }

    if (selectedPdf) setSelectedPdf(null);
    const nextPages = imageFiles.slice(0, availableSlots).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    const updatedPages = [...currentPages, ...nextPages];
    pagesRef.current = updatedPages;
    setPages(updatedPages);
    if (imageFiles.length > availableSlots) {
      toast.info(`Foram adicionadas somente ${availableSlots} páginas para respeitar o limite.`);
    }
  };

  const selectPdf = (file?: File) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      toast.error('Selecione um arquivo PDF válido.');
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      toast.error('O PDF deve ter no máximo 10MB.');
      return;
    }

    clearPages();
    setSelectedPdf(file);
  };

  const removePage = (id: string) => {
    setPages((current) => {
      const removed = current.find((page) => page.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      const updatedPages = current.filter((page) => page.id !== id);
      pagesRef.current = updatedPages;
      return updatedPages;
    });
  };

  const movePage = (index: number, direction: -1 | 1) => {
    setPages((current) => {
      const destination = index + direction;
      if (destination < 0 || destination >= current.length) return current;
      const next = [...current];
      [next[index], next[destination]] = [next[destination], next[index]];
      pagesRef.current = next;
      return next;
    });
  };

  const openCamera = () => cameraInputRef.current?.click();
  const openGallery = () => galleryInputRef.current?.click();

  const saveExam = async () => {
    if (!title.trim() || !examDate) {
      toast.error('Informe o título e a data do exame.');
      return;
    }
    if (!selectedPdf && pages.length === 0) {
      toast.error('Capture as páginas do exame ou selecione um PDF.');
      return;
    }

    setIsSubmitting(true);
    let uploadedPath = '';

    try {
      const file = selectedPdf || await createExamPdfFile(pages.map((page) => page.file), title);
      if (file.size > MAX_PDF_SIZE_BYTES) {
        throw new Error('O PDF gerado ultrapassou 10MB. Remova algumas páginas ou divida o exame.');
      }

      const upload = await MedicalRecordFileStorageService.uploadFile({
        clientId: selectedClient.clientId,
        patientId,
        file,
        user,
        selectedClient,
      });
      uploadedPath = upload.path;

      try {
        await createMedicalRecord({
          title: title.trim(),
          description: description.trim(),
          patientId,
          doctorId: user.userId,
          type: 'exam',
          date: new Date(examDate),
          attachments: [{
            name: file.name,
            url: '',
            path: upload.path,
            provider: upload.provider,
            type: 'pdf',
          }],
          uploadedBy: user.userId,
          uploadedByRole: selectedClient.userRole || user.userRole,
          uploadedByName: user.userDisplayName || '',
          clientId: selectedClient.clientId,
        });
      } catch (error) {
        await MedicalRecordFileStorageService.deleteFile(uploadedPath).catch((cleanupError) => {
          console.error('Não foi possível remover o arquivo órfão do exame:', cleanupError);
        });
        throw error;
      }

      toast.success('Exame anexado ao prontuário e incluído na timeline.');
      setIsOpen(false);
      resetForm();
      await onSaved?.();
    } catch (error) {
      console.error('Erro ao anexar exame:', error);
      toast.error(error instanceof Error ? error.message : 'Não foi possível anexar o exame.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-800 transition-colors hover:border-teal-300 hover:bg-teal-100"
          title="Fotografar ou anexar um exame ao prontuário do paciente"
        >
          <FileScan className="h-3.5 w-3.5" />
          Anexar exame
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/50 p-3 sm:p-5">
          <div className="max-h-[94vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 p-4 sm:p-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Anexar exame</h2>
                <p className="mt-1 text-sm text-slate-500">{patientName || 'Paciente selecionado'}</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={isSubmitting}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar anexo de exame"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-4 sm:p-5">
              <div className="rounded-xl border border-teal-200 bg-teal-50 px-3 py-2.5 text-sm text-teal-950">
                Fotografe cada página ou selecione várias imagens. As fotos serão reduzidas e reunidas em um único PDF, salvo também na área de Documentos.
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Título do exame *</label>
                  <input
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    className="w-full rounded-lg border-slate-300"
                    placeholder="Ex.: Radiografia de tórax"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Data e hora *</label>
                  <input
                    type="datetime-local"
                    value={examDate}
                    onChange={(event) => setExamDate(event.target.value)}
                    className="w-full rounded-lg border-slate-300"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Descrição</label>
                <textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={2}
                  className="w-full rounded-lg border-slate-300"
                  placeholder="Resumo ou observação sobre o exame (opcional)"
                />
              </div>

              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(event) => {
                  addImages(Array.from(event.target.files || []));
                  event.target.value = '';
                }}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(event) => {
                  addImages(Array.from(event.target.files || []));
                  event.target.value = '';
                }}
              />
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(event) => {
                  selectPdf(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />

              <div className="grid gap-2 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={openCamera}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-3 py-3 text-sm font-semibold text-teal-800 hover:bg-teal-100"
                >
                  <Camera className="h-4 w-4" /> Fotografar página
                </button>
                <button
                  type="button"
                  onClick={openGallery}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-3 text-sm font-semibold text-sky-800 hover:bg-sky-100"
                >
                  <Images className="h-4 w-4" /> Escolher fotos
                </button>
                <button
                  type="button"
                  onClick={() => pdfInputRef.current?.click()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <FileUp className="h-4 w-4" /> PDF pronto
                </button>
              </div>

              <p className="text-xs leading-relaxed text-slate-500">
                No celular, a câmera pode adicionar somente uma página por vez. Fotografe ou selecione novamente:
                as páginas anteriores permanecerão no mesmo exame.
              </p>

              {selectedPdf && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">{selectedPdf.name}</p>
                    <p className="text-xs text-slate-500">PDF pronto · {(selectedPdf.size / 1024 / 1024).toFixed(1)} MB</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedPdf(null)}
                    className="rounded-lg p-2 text-slate-500 hover:bg-white hover:text-red-600"
                    aria-label="Remover PDF selecionado"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}

              {pages.length > 0 && (
                <div>
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Páginas do exame</p>
                      <p className="text-xs text-slate-500">{pages.length}/{MAX_EXAM_PAGES} páginas adicionadas</p>
                    </div>
                    {pages.length < MAX_EXAM_PAGES && (
                      <div className="grid grid-cols-2 gap-2 sm:flex">
                        <button
                          type="button"
                          onClick={openCamera}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-2 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                        >
                          <Camera className="h-3.5 w-3.5" /> Fotografar próxima
                        </button>
                        <button
                          type="button"
                          onClick={openGallery}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-2 text-xs font-semibold text-sky-800 hover:bg-sky-100"
                        >
                          <Images className="h-3.5 w-3.5" /> Adicionar fotos
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {pages.map((page, index) => (
                      <div key={page.id} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                        <div className="relative aspect-[3/4] bg-slate-100">
                          <img src={page.previewUrl} alt={`Página ${index + 1}`} className="h-full w-full object-contain" />
                          <span className="absolute left-2 top-2 rounded-full bg-slate-900/75 px-2 py-0.5 text-xs font-semibold text-white">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex items-center justify-between border-t border-slate-200 p-1.5">
                          <button
                            type="button"
                            onClick={() => movePage(index, -1)}
                            disabled={index === 0}
                            className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-25"
                            aria-label={`Mover página ${index + 1} para trás`}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removePage(page.id)}
                            className="rounded p-1 text-slate-500 hover:bg-white hover:text-red-600"
                            aria-label={`Remover página ${index + 1}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => movePage(index, 1)}
                            disabled={index === pages.length - 1}
                            className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-25"
                            aria-label={`Mover página ${index + 1} para frente`}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 p-4 sm:flex-row sm:justify-end sm:p-5">
              <button
                type="button"
                onClick={closeModal}
                disabled={isSubmitting}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void saveExam()}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isSubmitting ? 'Preparando e enviando...' : 'Salvar exame'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExamUploadShortcut;
